import React, { useMemo, useEffect } from "react";
import Grid from "@mui/material/Grid";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import PropTypes from "prop-types";
import { useDispatch, useSelector } from "react-redux";
import { useMaterialUIController } from "context";
import { setFleetStats } from "../../../features/vehicle/vehicleSlice";
import { Skeleton } from "@mui/material";
import { FONT_SIZE, RADIUS, STATUS, GLASS } from "../theme/uiTokens";

// Small stroke icon set, drawn inline so every icon shares the same weight
// and can inherit the status gradient via currentColor.
const ICONS = {
  "@total": (
    <path d="M3 12l2-5h14l2 5M5 12v6a1 1 0 001 1h1a1 1 0 001-1v-1h8v1a1 1 0 001 1h1a1 1 0 001-1v-6M5 12h14M7.5 15.5h.01M16.5 15.5h.01" />
  ),
  "@running": <path d="M13 2L4.5 14h6L10 22l9-12h-6l0-8z" />,
  "@onroute": (
    <path d="M9 20l-5.5-2V5L9 7m0 13l6 2m-6-2V7m6 15l5.5-2V5L15 7m0 13V7m0 0L9 5" />
  ),
  "@idle": <path d="M12 7v5l3 3M12 21a9 9 0 100-18 9 9 0 000 18z" />,
  "@parked": (
    <path d="M6 17V7a1 1 0 011-1h6a4 4 0 010 8H9v3M4 20h16" />
  ),
  "@offline": (
    <path d="M12 2a9 9 0 100 18 9 9 0 000-18zM8 8l8 8M16 8l-8 8" />
  ),
};

function StatusIcon({ filterKey, color, size = 18 }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke={color}
      strokeWidth="1.9"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      {ICONS[filterKey] || <circle cx="12" cy="12" r="8" />}
    </svg>
  );
}

function FilterCard({ onFilterSelect, activeFilter }) {
  const dispatch = useDispatch();
  const [controller] = useMaterialUIController();
  const { darkMode } = controller;
  const surface = darkMode ? GLASS.dark : GLASS.light;

  // 👇 GETTING DATA DIRECTLY FROM REDUX
  const {
    myThings,
    latestUpdates
  } = useSelector((state) => state.fleet || {});

  const isLoading =
    !myThings ||
    !latestUpdates ||
    Object.keys(myThings).length === 0 ||
    Object.keys(latestUpdates).length === 0;

  const responsedata = useMemo(() => ({
    myThings: myThings || {},
    latestUpdates: latestUpdates || {}
  }), [myThings, latestUpdates]);

  const markerNumbers = useMemo(() => {
    const counts = {
      "@total": { statusKey: "total", title: "Total", number: 0 },
      "@running": { statusKey: "running", title: "Running", number: 0 },
      "@onroute": { statusKey: "onroute", title: "On Route", number: 0 },
      "@idle": { statusKey: "idle", title: "Idle", number: 0 },
      "@parked": { statusKey: "parked", title: "Parked", number: 0 },
      "@offline": { statusKey: "offline", title: "Offline", number: 0 },
    };

    const allThings = responsedata?.myThings || {};
    const allUpdates = responsedata?.latestUpdates || {};
    const now = Date.now();

    Object.keys(allUpdates).forEach((key) => {
      const latest =
        allUpdates[key]?.latest?.GPS || allUpdates[key]?.latest?.MOBILE;
      if (!latest) return;

      const marker = {
        speed: latest.speed || 0,
        raw: latest.raw || {},
        updatedAt: latest.updatedAt || 0,
      };

      counts["@total"].number += 1;

      if (marker.updatedAt + 3600000 < now) {
        marker.moveKey = "@offline";
        counts["@offline"].number += 1;
      } else if (marker.raw && marker.raw.IGNITION !== undefined) {
        if (marker.speed >= 0.25 && marker.raw.IGNITION === "1") {
          marker.moveKey = "@running";
          counts["@running"].number += 1;
        } else if (marker.speed >= 0.25 && marker.raw.IGNITION === "0") {
          marker.moveKey = "@parked";
          counts["@parked"].number += 1;
        } else if (marker.speed < 0.25 && marker.raw.IGNITION === "0") {
          marker.moveKey = "@parked";
          counts["@parked"].number += 1;
        } else if (marker.speed < 0.25 && marker.raw.IGNITION === "1") {
          marker.moveKey = "@idle";
          counts["@idle"].number += 1;
        }
      } else if (marker.speed <= 0.25) {
        marker.moveKey = "@parked";
        counts["@parked"].number += 1;
      } else {
        marker.moveKey = "@running";
        counts["@running"].number += 1;
      }

      if (allThings[key]?.journeyAvailable) {
        counts["@onroute"].number += 1;
      }
    });

    return counts;
  }, [responsedata]);

  useEffect(() => {
    if (markerNumbers) {
      const statsToSave = {
        total: markerNumbers["@total"].number,
        running: markerNumbers["@running"].number,
        idle: markerNumbers["@idle"].number,
        parked: markerNumbers["@parked"].number,
        offline: markerNumbers["@offline"].number,
        onRoute: markerNumbers["@onroute"].number,
      };

      dispatch(setFleetStats(statsToSave));
      localStorage.setItem("liveFleetStats", JSON.stringify(statsToSave));
    }
  }, [markerNumbers, dispatch]);

  // ---- Signature element: fleet-health spectrum bar ----
  // A single segmented bar built from the *real* running/idle/parked/offline
  // split — a live composition readout, not a decorative gradient.
  const spectrumSegments = useMemo(() => {
    const total = markerNumbers["@total"].number || 0;
    if (!total) return [];
    const order = ["@running", "@idle", "@parked", "@offline"];
    return order
      .map((key) => ({
        key,
        pct: (markerNumbers[key].number / total) * 100,
        color: STATUS[markerNumbers[key].statusKey].base,
      }))
      .filter((s) => s.pct > 0);
  }, [markerNumbers]);

  const renderCard = (key) => {
    const data = markerNumbers[key];
    const status = STATUS[data.statusKey];
    const isActive = activeFilter === key;

    return (
      <Grid item xs={6} sm={4} md={4} lg={4} display="flex" justifyContent="center" key={key}>
        <MDBox
          onClick={() => onFilterSelect(key)}
          sx={{
            position: "relative",
            width: "100%",
            borderRadius: `${RADIUS.md}px`,
            padding: "8px 10px",
            display: "flex",
            alignItems: "center",
            gap: "8px",
            cursor: "pointer",
            background: isActive ? status.soft : surface.panel,
            border: `1.5px solid ${isActive ? status.base : surface.border}`,
            boxShadow: isActive
              ? `0 4px 12px ${status.glow}`
              : "0 1px 6px rgba(15, 23, 42, 0.05)",
            transition: "transform 0.18s ease, box-shadow 0.18s ease, border-color 0.18s ease",
            overflow: "hidden",
            "&:hover": {
              transform: "translateY(-1px)",
              boxShadow: `0 6px 16px ${status.glow}`,
              borderColor: status.base,
            },
          }}
        >
          {/* thin gradient rail on the active card, reinforcing the brand thread */}
          {isActive && (
            <MDBox
              sx={{
                position: "absolute",
                top: 0,
                left: 0,
                right: 0,
                height: "2px",
                background: status.gradient,
              }}
            />
          )}

          <MDBox
            sx={{
              flexShrink: 0,
              width: 26,
              height: 26,
              borderRadius: `${RADIUS.sm}px`,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              background: status.gradient,
              boxShadow: `0 2px 6px ${status.glow}`,
            }}
          >
            <StatusIcon filterKey={key} color="#fff" size={14} />
          </MDBox>

          <MDBox display="flex" flexDirection="column" minWidth={0}>
            <MDTypography
              sx={{
                fontSize: FONT_SIZE.medium,
                fontWeight: 700,
                lineHeight: 1.1,
                fontVariantNumeric: "tabular-nums",
                color: isActive ? status.base : (darkMode ? "#fff" : "#1e293b"),
              }}
            >
              {data.number}
            </MDTypography>
            <MDTypography
              sx={{
                fontSize: "0.625rem",
                fontWeight: 600,
                letterSpacing: "0.03em",
                textTransform: "uppercase",
                color: darkMode ? "rgba(255,255,255,0.55)" : "rgba(30,41,59,0.55)",
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
            >
              {data.title}
            </MDTypography>
          </MDBox>
        </MDBox>
      </Grid>
    );
  };

  const renderSkeletonCards = () => {
    return Array.from({ length: 6 }).map((_, index) => (
      <Grid item xs={6} sm={4} md={4} lg={4} key={index}>
        <MDBox width="100%" sx={{ borderRadius: `${RADIUS.md}px`, overflow: "hidden" }}>
          <Skeleton variant="rounded" width="100%" height={42} sx={{ borderRadius: `${RADIUS.md}px` }} />
        </MDBox>
      </Grid>
    ));
  };

  return (
    <MDBox sx={{ width: "100%" }}>
      <Grid container spacing={0.75} wrap="wrap">
        {isLoading
          ? renderSkeletonCards()
          : Object.keys(markerNumbers).map((key) => renderCard(key))}
      </Grid>
    </MDBox>
  );
}

FilterCard.propTypes = {
  onFilterSelect: PropTypes.func.isRequired,
  activeFilter: PropTypes.string,
};

export default FilterCard;